import {Trans} from '@lingui/macro';
import React, {useState} from 'react';

import {IconButton} from '../../../libs/shared/ui/src/components/buttons/iconButton';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {openInBrowser} from '../../../srcCommon/browser/openInBrowser';
import {useConstant, useToggle} from '../../../srcCommon/helpers/hookHelpers';
import {isTeamNamespace} from '../../helpers/models/namespaceHelpers';
import {useAppDispatch, useAppSelector} from '../../helpers/reduxHelpers';
import {SettingsInboxesUrl} from '../../models/url/settingsUrlModel';
import {dismissSettingsInfoBoxAction} from '../../store/data/localPreferences/localPreferencesActions';
import {getFindShouldDisplayInfoBox} from '../../store/data/localPreferences/localPreferencesSelectors';
import {SettingsPanelId} from '../../store/panels/panelStateBase';
import {SettingsContentHeader} from '../common/settingsContentHeader';
import {
  StyledSettingsContent,
  StyledSettingsMain,
  StyledSettingsSidebar,
  StyledSettingsWrapper,
} from '../common/settingsWrapper';
import {SettingsHeader} from '../settingsHeader';
import {SettingsInboxesListTable} from './settingsInboxesListTable';
import {SettingsInboxesSidebar} from './settingsInboxesSidebar';

/*
 * Props.
 */

interface SettingsInboxesListProps {
  panelId: SettingsPanelId;
  url: SettingsInboxesUrl;
}

/*
 * Component.
 */

export const SettingsInboxesList = (props: SettingsInboxesListProps) => {
  const dispatch = useAppDispatch();
  const findShouldDisplayInfoBox = useAppSelector(getFindShouldDisplayInfoBox);
  // Sidebar state.
  const infoBoxId = useConstant(() =>
    isTeamNamespace(props.url.namespace) ? 'team_inbox' : 'individual_inbox',
  );
  const [isSidebarOpen, toggleSidebar] = useToggle(findShouldDisplayInfoBox(infoBoxId));
  const onInfoBoxCloseClick = () => {
    dispatch(dismissSettingsInfoBoxAction(infoBoxId));
    toggleSidebar();
  };
  const theme = useUserTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const onSearchQueryChange = (newQuery: string) => {
    setSearchQuery(newQuery);
  };

  return (
    <StyledSettingsWrapper>
      <SettingsHeader
        right={
          <IconButton
            iconName={isSidebarOpen ? 'infoFilled' : 'info'}
            label=""
            onClick={toggleSidebar}
            color={isSidebarOpen ? theme.palette.blue.shade40 : theme.greys.shade70}
            hoverColor={isSidebarOpen ? theme.palette.blue.shade40 : theme.greys.shade70}
          />
        }
      >
        <Trans id="settings-inboxes-header">Inboxes</Trans>
      </SettingsHeader>
      <StyledSettingsMain>
        <StyledSettingsContent>
          <SettingsContentHeader
            searchInputValue={searchQuery}
            onSearchInput={onSearchQueryChange}
            onExitClick={() => {
              setSearchQuery('');
            }}
          />
          <SettingsInboxesListTable searchQuery={searchQuery} url={props.url} />
        </StyledSettingsContent>
        {isSidebarOpen && (
          <StyledSettingsSidebar>
            <SettingsInboxesSidebar
              namespace={props.url.namespace}
              onCloseClick={onInfoBoxCloseClick}
              onFooterClick={() => {
                if (isTeamNamespace(props.url.namespace)) {
                  openInBrowser('https://help.frontapp.com/t/q5249n/adding-and-using-team-inboxes');
                } else {
                  openInBrowser('https://help.frontapp.com/t/y7621v/understanding-individual-inboxes');
                }
              }}
            />
          </StyledSettingsSidebar>
        )}
      </StyledSettingsMain>
    </StyledSettingsWrapper>
  );
};
